import React, { useEffect, useMemo, useState } from 'react';
import { logger } from '@utils/logger';
import { find, isEmpty, isNil, map, some } from 'lodash';
import { ContentMatrix } from '@components/content-matrix';
import { restaurantPropTypes } from '@nandosaus/prop-types';
import PropTypes from 'prop-types';
import { MARKET } from '@nandosaus/constants';
import { observer } from 'mobx-react';
import { RestaurantModel, useRootStore } from '@nandosaus/state-management';
import { useFeature } from '@hooks/use-feature';
import { ContentImageBlock } from '@components/content-image';
import { ContentBlock } from '@components/content-block';
import { useMarket } from '@hooks/use-market';
import { PageHeader } from '@components/page-header';
import styled from 'styled-components';
import { Flex } from '@components/grid';
import { RestaurantComingSoonBanner } from '@components/restaurant-coming-soon-banner';
import { RestaurantStatus } from '@components/restaurant-status';
import { DeliveryOptionsBlock } from '../delivery-options-block';
import { RestaurantDetailsTables } from '../restaurant-details-tables';
import { RestaurantOrderButton } from '../restaurant-order-button';

const CONTENT_IMAGE = [
  {
    url: '/static/images/restaurant/catering.jpg',
    alt: 'Restaurant Catering Packages',
  },
];

const VIEW_BUTTON = {
  text: 'View our catering packages',
  href: 'https://catering.nandos.com.au/',
};

const StatusHeaderBar = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: center;
  padding-top: 1rem;
  padding-bottom: 1rem;
  background-color: transparent;
`;

const RESTAURANT_NAME_TOKEN = /\$restaurantName/g;

const replaceRestaurantName = (value, restaurantName) =>
  typeof value === 'string' && restaurantName ? value.replace(RESTAURANT_NAME_TOKEN, restaurantName) : value;

const substituteContentImageBlock = (block, restaurantName) => ({
  ...block,
  heading: replaceRestaurantName(block.heading, restaurantName),
  body: replaceRestaurantName(block.body, restaurantName),
  button: map(block.button, child => ({
    ...child,
    buttonText: replaceRestaurantName(child.buttonText, restaurantName),
  })),
});

const substituteRestaurantNameInMatrix = (matrix, restaurantName) =>
  map(matrix, block =>
    block.typeHandle === 'contentImageBlock' ? substituteContentImageBlock(block, restaurantName) : block
  );

const matrixHasDeliveryOptionsAnchor = matrix => some(matrix, block => block.typeHandle === 'deliveryOptionsBlock');

const RestaurantPageContent = ({ restaurant, restaurantPageContent }) => {
  const { RestaurantDetailsState, RestaurantStore } = useRootStore();

  const showCmsRestaurantPages = useFeature('show-cms-restaurant-pages');

  const cateringEnabled = useFeature('catering-enabled');

  const comingSoonRestaurants = useFeature('coming-soon-restaurant-whitelist');
  const comingSoon = comingSoonRestaurants?.restaurantIds?.includes(parseInt(restaurant.id, 10)) || false;

  const currentMarket = useMarket();

  const chosenRestaurant = useMemo(() => {
    if (RestaurantDetailsState.restaurant) {
      return RestaurantDetailsState.restaurant;
    }

    return RestaurantModel.create(restaurant);
  }, [RestaurantDetailsState.restaurant, restaurant]);

  const imageUrl = `https://assets.nandos.com.au/Nandos-${
    currentMarket === MARKET.AUSTRALIA ? 'Australia' : 'New-Zealand'
  }/images/Restaurant-Images/${chosenRestaurant.id}-restaurant-hero-header.png`;

  const headerImage = { url: imageUrl, alt: `Nandos ${chosenRestaurant.name} building image` };

  const fallbackHeaderImage = {
    url: `https://assets.nandos.com.au/Nandos-Australia/images/Restaurant-Images/fallback-restaurant-hero-header-image.png`,
    alt: `Nandos building image`,
  };

  const [imageAvailable, setImageAvailable] = useState(true);

  useEffect(() => {
    if (!imageUrl) {
      setImageAvailable(false);
      return;
    }
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => setImageAvailable(true);
    img.onerror = () => setImageAvailable(false);
  }, [imageUrl]);

  useEffect(() => {
    const updateDynamicRestaurantData = async () => {
      await RestaurantStore.loadRestaurants();

      const selected = find(RestaurantStore.asArray, ({ id }) => id === restaurant.id);

      if (selected) {
        RestaurantDetailsState.updateRestaurant(selected);
      } else {
        logger.error('Error updating dynamic restaurant data.', { restaurant });
      }
    };

    updateDynamicRestaurantData();
  }, [RestaurantDetailsState, RestaurantStore, restaurant, restaurant.id]);

  const content =
    restaurantPageContent && showCmsRestaurantPages
      ? {
          heading: replaceRestaurantName(restaurantPageContent.heading, chosenRestaurant.name),
          subheading: replaceRestaurantName(restaurantPageContent.subheading, chosenRestaurant.name),
          contentMatrix: substituteRestaurantNameInMatrix(restaurantPageContent.contentMatrix, chosenRestaurant.name),
          contentBlock: {
            blockTitle: replaceRestaurantName(restaurantPageContent.description, chosenRestaurant.name),
            body: replaceRestaurantName(restaurantPageContent.body, chosenRestaurant.name),
          },
        }
      : {
          heading: `Nandos ${chosenRestaurant.name}`,
          subheading: undefined,
          contentMatrix: [],
          contentBlock: undefined,
        };

  const hasMatrixDeliveryAnchor = matrixHasDeliveryOptionsAnchor(content.contentMatrix);

  return (
    <>
      <PageHeader
        eyebrow=""
        heading={content.heading}
        contentPosition="center"
        contentAlignment="center"
        image={imageAvailable ? headerImage : fallbackHeaderImage}
        tintOpacity={0.2}
        description=""
      >
        <RestaurantOrderButton restaurant={chosenRestaurant} />
      </PageHeader>

      {comingSoon && <RestaurantComingSoonBanner address={chosenRestaurant.formattedAddress} />}
      {!comingSoon && showCmsRestaurantPages && (
        <StatusHeaderBar>
          <RestaurantStatus restaurant={chosenRestaurant} displayHeaderBar />
        </StatusHeaderBar>
      )}

      {!comingSoon && <RestaurantDetailsTables restaurant={chosenRestaurant} />}

      {!hasMatrixDeliveryAnchor &&
        !isEmpty(chosenRestaurant.formattedDeliveryServices) &&
        !isNil(chosenRestaurant.orderingEnabled) && (
          <DeliveryOptionsBlock
            deliveryServices={chosenRestaurant.formattedDeliveryServices}
            orderingEnabled={chosenRestaurant.orderingEnabled}
            hasBackgroundDecal={!showCmsRestaurantPages}
          />
        )}

      {!showCmsRestaurantPages && cateringEnabled && currentMarket === MARKET.AUSTRALIA && (
        <ContentImageBlock
          heading="Catering Packages"
          body="Need to feed the crew? Nando’s catering packages will spice up any event."
          image={CONTENT_IMAGE}
          button={VIEW_BUTTON}
        />
      )}
      {showCmsRestaurantPages && content.contentBlock?.blockTitle && <ContentBlock {...content.contentBlock} />}
      {showCmsRestaurantPages && !!content.contentMatrix.length && (
        <ContentMatrix hasExternalHeader blocks={content.contentMatrix} restaurant={chosenRestaurant} />
      )}
    </>
  );
};

RestaurantPageContent.propTypes = {
  restaurantPageContent: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    restaurantId: PropTypes.number.isRequired,
    heading: PropTypes.string,
    subheading: PropTypes.string,
    description: PropTypes.string,
    body: PropTypes.string,
    contentMatrix: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }),
  restaurant: restaurantPropTypes.isRequired,
};

RestaurantPageContent.defaultProps = {
  restaurantPageContent: undefined,
};

export default observer(RestaurantPageContent);
